import { combineReducers } from 'redux';
import { reducer as formReducer } from 'redux-form';
import audiencesTableState from '../modules/audiencesTableState/reducers';
import chatbotSidebarState from '../modules/chatbotSidebarState/reducers';
import connectionsTableState from '../modules/connectionsTableState/reducers';
import contactTableState from '../modules/contactTableState/reducers';
import mailchimpConnectionsTableState from '../modules/mailchimpConnectionsTableState/reducers';
import modalState from '../modules/modalsState/reducers';
import onboardingState from '../modules/onboardingState/reducers';
import recipientsTableState from '../modules/recipientsTableState/reducers';
import registerFlowState from '../modules/registerFlowState/reducers';
import rssFeedTableState from '../modules/rssFeedTableState/reducers';
import { StoreState } from '../store-interfaces';
import adAccounts from './adAccounts';
import adAudiences from './adAudiences';
import adConfigs from './adConfigs';
import adEntities from './adEntities';
import affiliates from './affiliates';
import { aiEmailTemplates } from './aiEmailTemplates';
import { ambassadorInfo } from './ambassadorInfo';
import analytics from './analytics';
import apiKeys from './apiKeys';
import attribute from './attribute';
import audiences from './audiences';
import { automation } from './automation';
import { botAnalytics } from './botAnalytics';
import campaignPurposes from './campaignPurposes';
import campaigns from './campaigns';
import { campaignsOverview } from './campaignsOverview';
import chatbot from './chatbot';
import { chatStarters } from './chatStarters';
import { chatWidgetTroubleshooting } from './chatWidgetTroubleshooting';
import company from './company';
import companyUsers from './companyUsers';
import connectedPage from './connectedPage';
import connections from './connections';
import contacts from './contacts';
import { contactsByAudienceId } from './contactsByAudienceId';
import ctmAds from './ctmAds';
import { ctmInsights } from './ctmInsights';
import dataQualityTest from './dataQualityTest';
import domain from './domain';
import { emailAccounts } from './emailAccounts';
import env from './env';
import errors from './errors';
import esp from './esp';
import facebook from './facebook';
import { fbOauthTokens } from './fbOauthTokens';
import fbPost from './fbPost';
import { featureFlags } from './featureFlags';
import { followUps } from './followUps';
import { glockApp } from './glockApp';
import { goHighLevel } from './goHighLevel';
import { googleAds } from './googleAds';
import { googleSheets } from './googleSheets';
import { guides } from './guides';
import { iceBreakers } from './iceBreakers';
import { instagram } from './instagram';
import invite from './invite';
import { klaviyo } from './klaviyo';
import landingPages from './landingPages';
import leadAssignmentGroups from './leadAssignmentGroups';
import leadAssignments from './leadAssignments';
import leads from './leads';
import { mailchimp } from './mailchimp';
import mailchimpConnections from './mailchimpConnections';
import mainMenu from './mainMenu';
import mandatoryUpdateBanner from './mandatoryUpdateBanner';
import messengerAds from './messengerAds';
import myLeads from './myLeads';
import network from './network';
import notes from './notes';
import notifications from './notifications';
import page from './page';
import pageGroup from './pageGroup';
import partners from './partners';
import { personas } from './personas';
import promoter from './promoter';
import promotions from './promotions';
import rssFeed from './rssFeed';
import { salesForce } from './salesForce';
import sampleBots from './sampleBots';
import sendGrid from './sendGrid';
import { sendlane } from './sendlane';
import session from './session';
import { shopify } from './shopify';
import { signalConfiguration } from './signalConfiguration';
import { signalSourceConfigurations } from './signalSourceConfigurations';
import { signalVisitedUrlEventTriggers } from './signalVisitedUrlEventTriggers';
import signUpForm from './signUpForm';
import { slack } from './slack';
import sponsoredMessages from './sponsoredMessage';
import subscription from './subscription';
import suggestion from './suggestion';
import { superpixelConfiguration } from './superpixelConfiguration';
import { supportItems } from './supportItems';
import { suppressionLists } from './suppressionLists';
import tags from './tags';
import { templates } from './templates';
import trigger from './trigger';
import twilio from './twilio';
import { userGeodata } from './userGeodata';
import variables from './variables';
import view from './view';
import { visitor } from './visitor';
import voyagerLeads from './voyagerLeads';
import websiteReportSummary from './websiteReportSummary';
import { whatsNew } from './whatsNew';
import widget from './widget';
import { xrayConfigurations } from './xrayConfigurations';

const rootReducer = combineReducers<StoreState>({
  adAccounts,
  adAudiences,
  adConfigs,
  adEntities,
  affiliates,
  aiEmailTemplates,
  ambassadorInfo,
  analytics,
  apiKeys,
  attribute,
  audiences,
  automation,
  audiencesTableState,
  botAnalytics,
  campaignPurposes,
  campaigns,
  campaignsOverview,
  chatbot,
  chatbotSidebarState,
  chatStarters,
  chatWidgetTroubleshooting,
  company,
  companyUsers,
  connectedPage,
  connections,
  contacts,
  connectionsTableState,
  mailchimpConnectionsTableState,
  contactsByAudienceId,
  contactTableState,
  ctmAds,
  ctmInsights,
  domain,
  dataQualityTest,
  emailAccounts,
  env,
  errors,
  esp,
  facebook,
  fbPost,
  fbOauthTokens,
  featureFlags,
  followUps,
  form: formReducer,
  goHighLevel,
  googleSheets,
  glockApp,
  guides,
  googleAds,
  iceBreakers,
  instagram,
  invite,
  landingPages,
  leadAssignmentGroups,
  leadAssignments,
  leads,
  mainMenu,
  mandatoryUpdateBanner,
  messengerAds,
  modalState,
  myLeads,
  network,
  notes,
  notifications,
  onboarding: onboardingState,
  page,
  pageGroup,
  partners,
  personas,
  promoter,
  promotions,
  recipientsTableState,
  registerFlowState,
  rssFeed,
  rssFeedTableState,
  sampleBots,
  session,
  signUpForm,
  sponsoredMessages,
  subscription,
  suggestion,
  shopify,
  tags,
  templates,
  trigger,
  twilio,
  variables,
  view,
  voyagerLeads,
  websiteReportSummary,
  whatsNew,
  widget,
  mailchimp,
  mailchimpConnections,
  slack,
  klaviyo,
  sendGrid,
  salesForce,
  sendlane,
  userGeodata,
  suppressionLists,
  xrayConfigurations,
  signalVisitedUrlEventTriggers,
  signalSourceConfigurations,
  signalConfiguration,
  superpixelConfiguration,
  supportItems,
  visitor,
});

export default rootReducer;
